import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import type {ControlledTooltipProps} from '@github-ui/portal-tooltip/controlled'
import {ProfileReference} from '@github-ui/profile-reference'
import {DotFillIcon} from '@primer/octicons-react'
import {Label, Link, RelativeTime} from '@primer/react'
import type {SxProp} from '@primer/styled-react'
import {Box} from '@primer/styled-react'
import {clsx} from 'clsx'
import {Suspense} from 'react'

import {LABELS} from '../constants/labels'
import {TEST_IDS} from '../constants/test-ids'
import styles from './ActivityHeader.module.css'
import avatarStyles from './Avatar.module.css'
import {CommentAuthorAssociation} from './CommentAuthorAssociation'
import {CommentHeaderBadge} from './CommentHeaderBadge'
import {CommentSubjectAuthor} from './CommentSubjectAuthor'
import type {CommentAuthorAssociation as GraphQlCommentAuthorAssociation} from './issue-comment/__generated__/IssueCommentHeader.graphql'
import {PinnedByBadge} from './PinnedByBadge'
import {SpammyBadge} from './SpammyBadge'
import {SponsorsBadge} from './SponsorsBadge'

type ActivityData = {
  authorAssociation: GraphQlCommentAuthorAssociation
  id: string
  createdAt: string
  isHidden: boolean
  minimizedReason: string | null | undefined
  pendingMinimizeReason?: string | null
  showSpammyBadge?: boolean
  repository: {
    id: string
    isPrivate: boolean
    name: string
    owner: {
      login: string
      url: string
    }
  }
  url: string
  createdViaEmail?: boolean
  viaApp?: {
    name: string
    url: string
  }
  authorToRepoOwnerSponsorship?: {
    createdAt: string
    isActive: boolean
  } | null
  state?: string
}

export type ActivityHeaderHeadingProps = {
  as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export const CommentAuthorTypes = {
  Bot: 'Bot',
  Mannequin: 'Mannequin',
  User: 'User',
} as const

export type CommentAuthorType = (typeof CommentAuthorTypes)[keyof typeof CommentAuthorTypes]

type ActivityHeaderProps = {
  actions?: React.ReactElement
  additionalHeaderMessage?: React.ReactElement
  additionalUserIcon?: React.ReactElement
  badges?: React.ReactElement
  lastEditedByMessage?: React.ReactElement
  editHistoryComponent?: React.ReactElement
  avatarUrl: string
  comment: ActivityData
  commentSubjectAuthorLogin?: string
  commentSubjectType?: string
  commentRef?: React.RefObject<HTMLDivElement | null>
  isMinimized: boolean
  commentAuthorLogin: string
  commentAuthorProfileName?: string | null
  commentAuthorSlug?: string
  userAvatar?: React.ReactElement
  viewerDidAuthor?: boolean
  tooltipDirection?: ControlledTooltipProps['direction']
  isReply?: boolean
  commentAuthorType?: CommentAuthorType
  headingProps?: ActivityHeaderHeadingProps
  id?: string
  forceInlineAvatar?: boolean
  containerStyle?: SxProp
  threadCommentCount?: number
  commentBody?: string
  isInDialogMode?: boolean
  originalDiffPathUri?: string | null
  isOutdated?: boolean
  hideProfileLink?: boolean
  pinnedByLogin?: string | null
} & SxProp

function getOutdatedCommentUrl(originalDiffPathUri?: string | null) {
  if (!originalDiffPathUri) return undefined
  const url = new URL(originalDiffPathUri, window.location.origin)
  // Adding these params because without them it will navigate to the classic experience
  url.searchParams.set('new_files_changed', 'true')
  return url.toString()
}

/**
 * Abstraction of a comment header that can be used in different contexts.
 */
export function ActivityHeader({
  actions,
  additionalUserIcon,
  badges,
  comment,
  commentAuthorType,
  commentAuthorSlug,
  commentAuthorLogin,
  commentAuthorProfileName,
  isMinimized = false,
  avatarUrl,
  lastEditedByMessage,
  editHistoryComponent,
  additionalHeaderMessage,
  sx,
  viewerDidAuthor = false,
  userAvatar,
  commentSubjectAuthorLogin,
  commentSubjectType,
  isReply = false, // eslint-disable-next-line @eslint-react/no-unstable-default-props
  headingProps: {as: HeadingElement, ...headingProps} = {
    as: 'h3',
  },
  id,
  forceInlineAvatar,
  containerStyle,
  originalDiffPathUri,
  isOutdated,
  hideProfileLink = false,
  pinnedByLogin,
  tooltipDirection,
}: ActivityHeaderProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const containerStyles =
    containerStyle || isReply
      ? {}
      : {
          backgroundColor: viewerDidAuthor ? 'accent.subtle' : 'canvas.subtle',
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
          borderBottom: '1px solid',
          borderColor: viewerDidAuthor ? 'accent.muted' : 'border.default',
          borderBottomWidth: isMinimized ? 0 : 1,
          py: forceInlineAvatar ? [1, 1, 2, 2] : 1,
          pr: 1,
          pl: 3,
          ...sx,
        }
  const commentMinimizedReason = comment.pendingMinimizeReason ?? comment.minimizedReason
  const commentHidden = !!commentMinimizedReason || comment.isHidden
  const createdAt = new Date(comment.createdAt)

  const isBot = commentAuthorType === CommentAuthorTypes.Bot
  const isMannequin = commentAuthorType === CommentAuthorTypes.Mannequin
  const isCopilot =
    isBot && ((commentAuthorLogin === 'Copilot' && !!commentAuthorSlug) || commentAuthorLogin === 'copilot-swe-agent')

  const profileUrl =
    isMannequin || hideProfileLink ? undefined : `/${isBot ? 'apps/' : ''}${commentAuthorSlug ?? commentAuthorLogin}`
  const profileLogin = isCopilot ? 'Copilot' : commentAuthorLogin
  let hovercardAttributes: Record<string, string> = {}
  if (isCopilot) {
    hovercardAttributes = getHovercardAttributesForType('copilot', {botSlug: commentAuthorSlug ?? commentAuthorLogin})
  } else if (!isMannequin) {
    hovercardAttributes = getHovercardAttributesForType('user', {login: commentAuthorLogin})
  }

  const showPinnedByBadge = pinnedByLogin && !commentHidden

  const subText = (
    <>
      {additionalHeaderMessage && <span className={styles.HeaderMutedText}>{additionalHeaderMessage}</span>}
      <span className={styles.HeaderMutedText}>
        <Link
          href={isOutdated ? getOutdatedCommentUrl(originalDiffPathUri) : comment.url}
          data-turbo={isOutdated ? 'false' : 'true'}
          className={styles.HeaderLink}
        >
          <RelativeTime date={createdAt}>
            on {createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
          </RelativeTime>
        </Link>
        {comment.createdViaEmail && <span>{` ${LABELS.sentViaEmail}`}</span>}
        {comment.viaApp && (
          <span>
            {' '}
            – with{' '}
            <Link inline href={comment.viaApp.url}>
              {comment.viaApp.name}
            </Link>
          </span>
        )}
      </span>
      {commentHidden && (
        <span className={styles.HeaderMutedTextNoEllipsis}>
          {' '}
          &middot;{' '}
          {commentMinimizedReason ? (
            <span>
              {LABELS.hiddenCommentWithReason} {commentMinimizedReason.replace(/_/g, '-').toLowerCase()}
            </span>
          ) : (
            <span>{LABELS.hiddenComment}</span>
          )}
        </span>
      )}
      {lastEditedByMessage && !commentHidden && <Suspense>{lastEditedByMessage}</Suspense>}
      {showPinnedByBadge && <PinnedByBadge pinnedByLogin={pinnedByLogin} />}
    </>
  )
  return (
    <Box
      id={id}
      sx={{
        ...containerStyles,
      }}
      data-testid={TEST_IDS.commentHeader}
      className={styles.ActivityHeaderContainer}
    >
      <Box
        className={clsx(styles.activityHeader, styles.ActivityHeaderGridLayout)}
        sx={{
          // isXSmall &&
          // if there is no edit history row and the avatar is not inline, offset the spacing
          paddingBottom: !lastEditedByMessage && !forceInlineAvatar ? 'var(--base-size-6, 6px)' : 0,
        }}
      >
        <HeadingElement {...headingProps} className="sr-only">
          <>
            {isCopilot ? 'Copilot' : commentAuthorLogin} {isReply ? 'replied' : 'commented'}{' '}
            <RelativeTime date={createdAt}>
              on {createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}{' '}
            </RelativeTime>
          </>
        </HeadingElement>
        <Box
          className={clsx(forceInlineAvatar ? '' : avatarStyles.avatarInner, styles.AvatarContainer)}
          sx={{
            display: forceInlineAvatar ? 'flex' : ['flex', 'flex', 'none', 'none'],
          }}
        >
          {userAvatar ?? (
            <DefaultAvatar
              avatarUrl={avatarUrl}
              login={commentAuthorLogin}
              isCopilot={isCopilot}
              isHidden={commentHidden}
              hovercardAttributes={hovercardAttributes}
              profileUrl={profileUrl}
            />
          )}
          {additionalUserIcon}
        </Box>
        <div
          className={clsx(styles.narrowViewportWrapper, styles.CommentHeaderContentContainer)}
          data-testid={TEST_IDS.commentHeaderLeftSideItems}
        >
          <div className={styles.TitleContainer}>
            {profileUrl ? (
              <Link
                href={profileUrl}
                data-testid={TEST_IDS.avatarLink}
                {...hovercardAttributes}
                className={clsx(
                  styles.AuthorName,
                  styles.AuthorLink,
                  commentHidden ? 'color-fg-muted' : 'color-fg-default',
                )}
              >
                <ProfileReference login={profileLogin} profileName={commentAuthorProfileName} isAgent={isCopilot} />
              </Link>
            ) : (
              // We use a span to avoid accessibility issues with empty links
              <span
                data-testid={TEST_IDS.avatarName}
                {...hovercardAttributes}
                className={clsx(styles.AuthorName, commentHidden ? 'color-fg-muted' : 'color-fg-default')}
              >
                <ProfileReference login={profileLogin} profileName={commentAuthorProfileName} isAgent={isCopilot} />
              </span>
            )}

            {(isBot || isMannequin) && (
              <Label variant="secondary" className="ml-1">
                {LABELS.authorLabel(isBot, isCopilot)}
              </Label>
            )}
          </div>
          <div className={clsx(styles.footer, styles.FooterContainer)}>{subText}</div>
        </div>
        <div
          data-testid={TEST_IDS.commentHeaderRightSideItems}
          className={clsx(styles.narrowViewportWrapper, styles.ActionsContainer)}
        >
          {editHistoryComponent && <div className={styles.EditsContainer}>{editHistoryComponent}</div>}
          <div className={styles.BadgesContainer}>
            <div className={styles.BadgesGroupContainer}>
              {comment.state?.toUpperCase() === 'PENDING' && (
                <CommentHeaderBadge
                  leadingElement={<DotFillIcon />}
                  label="Pending"
                  ariaLabel="This review comment is pending"
                  testId="pending-badge"
                  variant="attention"
                  className={styles.CommentHeaderBadge}
                />
              )}
              {!commentHidden &&
                !isMinimized &&
                comment.authorToRepoOwnerSponsorship &&
                comment.authorToRepoOwnerSponsorship.isActive && (
                  <SponsorsBadge
                    createdAt={comment.authorToRepoOwnerSponsorship.createdAt}
                    owner={comment.repository.owner.login}
                    viewerDidAuthor={viewerDidAuthor}
                  />
                )}
              {!commentHidden && !isMinimized && comment.showSpammyBadge && <SpammyBadge />}
              {!comment.repository.isPrivate && !commentHidden && !isMinimized && (
                <CommentAuthorAssociation
                  association={comment.authorAssociation}
                  viewerDidAuthor={viewerDidAuthor}
                  org={comment.repository.owner.login}
                  repo={comment.repository.name}
                  tooltipDirection={tooltipDirection}
                />
              )}
              {commentAuthorLogin === commentSubjectAuthorLogin && (
                <CommentSubjectAuthor
                  viewerDidAuthor={viewerDidAuthor}
                  subjectType={commentSubjectType}
                  tooltipDirection={tooltipDirection}
                />
              )}
              {badges}
            </div>
            <div className={styles.ActionsButtonsContainer}>{actions}</div>
          </div>
        </div>
      </Box>
    </Box>
  )
}

function DefaultAvatar({
  avatarUrl,
  login,
  isHidden,
  isCopilot,
  hovercardAttributes,
  profileUrl,
}: {
  avatarUrl: string
  login: string
  isHidden: boolean
  isCopilot: boolean
  hovercardAttributes: Record<string, string>
  profileUrl?: string
}) {
  const avatarElement = (
    <GitHubAvatar
      src={avatarUrl}
      alt={isCopilot ? 'Copilot' : `@${login}`}
      size={24}
      square={isCopilot}
      className={isHidden ? avatarStyles.hiddenActivityAvatar : avatarStyles.activityAvatar}
    />
  )

  if (profileUrl) {
    return (
      <Link
        href={profileUrl}
        className={avatarStyles.avatarLink}
        {...hovercardAttributes}
        aria-label={isCopilot ? 'Copilot' : `@${login}'s profile`}
      >
        {avatarElement}
      </Link>
    )
  }

  // For non-interactive avatars (mannequins, etc.), don't use link with aria-label to prevent accessibility issues
  return (
    <div className={avatarStyles.avatarWithoutLink} {...hovercardAttributes}>
      {avatarElement}
    </div>
  )
}
